import {
  Attestation,
  AttestationDraft,
  AttestationDraftPayload,
  AttestationDraftResponse,
  AttestationFormData,
  AttestationStatus,
  AttestationResponse,
  AttestationStatusResponse,
  AttestationSubmitResponse,
  AttestationsListResponse,
} from '@/types/attestation'
import { apiClient } from './api'

/**
 * `POST /attestations/{id}/submit` answered 400 because the draft is not
 * finished, carrying the *names* of what is still missing.
 *
 * Thrown rather than returned so a submit call site cannot forget to check it,
 * and typed separately from a generic Error so the form can render the fields
 * inline instead of dumping a banner at the user.
 */
export class AttestationIncompleteError extends Error {
  /**
   * Server field names, verbatim. Mostly one name per entry, but the
   * institutional qualification requirement arrives as a single composite entry:
   * `"accredited_investor_status or qualified_institutional_buyer or institutional_customer"`
   * — the entity must claim at least one of the three, and the server has no way
   * to say which. Callers must expand that form rather than assume one key.
   */
  readonly missingFields: string[]

  constructor(message: string, missingFields: string[]) {
    super(message)
    this.name = 'AttestationIncompleteError'
    this.missingFields = missingFields
  }
}

/** Read `missing_fields` off an error body without trusting its shape. */
const readMissingFields = (body: unknown): string[] => {
  if (!body || typeof body !== 'object') return []
  const raw = (body as Record<string, unknown>).missing_fields
  if (!Array.isArray(raw)) return []
  return raw.filter((entry): entry is string => typeof entry === 'string')
}

class AttestationService {
  private api = apiClient

  async getAttestationStatus(): Promise<AttestationStatus> {
    try {
      const response = await this.api.get<AttestationStatusResponse>('/attestations/status')

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to get attestation status')
      }

      const status = response.data.data
      // 🔴 Normalize at the service boundary. A backend that predates the
      // `pending_attestation` key omits it entirely, and `undefined !== null`
      // would send every un-attested user to the KYB terminal panel instead of
      // the signup funnel. With the coalesce, an old backend degrades to
      // exactly the pre-KYB behavior. Every consumer (the route guards and
      // AttestationScreen) tests this normalized value.
      return { ...status, pending_attestation: status.pending_attestation ?? null }
    } catch (error) {
      console.error('Get attestation status failed:', error)
      throw error
    }
  }

  /**
   * The caller's in-progress DRAFT, or `null` when there is none.
   *
   * Pass `organizationId` for the *entity* draft (OWNER/ADMIN only). The two are
   * separate rows: a user can hold a personal draft and draft for their org at
   * the same time.
   *
   * `null` is a normal answer, not an error — and it also means "already
   * submitted", because a submitted row is no longer a DRAFT and drops out of
   * this endpoint.
   */
  async getDraft(organizationId?: string): Promise<AttestationDraft | null> {
    const response = await this.api.get<AttestationDraftResponse>('/attestations/draft', {
      params: organizationId ? { organization_id: organizationId } : undefined,
    })
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load your saved progress')
    }
    return response.data.data ?? null
  }

  /**
   * Upsert a partial draft. An empty payload is legal and creates the row.
   *
   * Only the keys present are written — omitting a field leaves the stored value
   * alone. `status` / `subject_type` / `submitted_at` / `user_id` are not
   * writable and are dropped server-side, so `AttestationDraftPayload` does not
   * offer them.
   */
  async saveDraft(payload: AttestationDraftPayload = {}): Promise<AttestationDraft> {
    const response = await this.api.put<AttestationDraftResponse>('/attestations/draft', payload)
    if (!response.success || !response.data?.data) {
      throw new Error(response.error || 'Failed to save your progress')
    }
    return response.data.data
  }

  /**
   * Finish a draft: DRAFT → COMPLETE, or → PENDING_VERIFICATION for an org whose
   * claimed domain is not yet proven.
   *
   * Throws `AttestationIncompleteError` when the server says the draft is not
   * finished, so the caller can map the field names onto the form.
   */
  async submitDraft(attestationId: string): Promise<AttestationDraft> {
    const response = await this.api.post<AttestationSubmitResponse>(
      `/attestations/${attestationId}/submit`,
    )
    if (!response.success || !response.data?.data) {
      const missingFields = readMissingFields(response.errorBody)
      if (missingFields.length) {
        throw new AttestationIncompleteError(
          response.error || 'Attestation is incomplete',
          missingFields,
        )
      }
      throw new Error(response.error || 'Failed to submit your attestation')
    }
    return response.data.data
  }

  /**
   * @deprecated The legacy single-shot create. Both signup flows now go through
   * `saveDraft` + `submitDraft` so an abandoned form can be resumed. Kept
   * because it is still a documented public endpoint.
   */
  async createAttestation(formData: AttestationFormData): Promise<Attestation> {
    try {
      const response = await this.api.post<AttestationResponse>('/attestations', formData)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create attestation')
      }

      return response.data.data
    } catch (error) {
      console.error('Create attestation failed:', error)
      throw error
    }
  }

  async getUserAttestations(): Promise<Attestation[]> {
    try {
      const response = await this.api.get<AttestationsListResponse>('/attestations')

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to get user attestations')
      }

      return response.data.data
    } catch (error) {
      console.error('Get user attestations failed:', error)
      throw error
    }
  }
}

export const attestationService = new AttestationService()
